import { Injectable } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { CompanyStorage } from 'src/app/_core/company-storage';
import { ReceiptFiscalDocumentUpdate, readDocumentIdentity } from './fake-prro';

// fakePrro bookkeeping on the device. TaxService writes it; TaxRegisterWorker reads it.
// - Fake documents, by UID: documents numbered with made-up credentials. They never reach the
//   backend outbox; each keeps the receipt it was issued for and the made-up number it got, so
//   its real version can update that receipt once the tax server has registered it.
// - Made-up offline session ids: a document saved in such a session is fake too.
// - Deliveries: the real versions of fake documents, waiting to reach the backend.
export interface FakePrroDocument {
  uid: string;
  taxRegisterFiscalNumber: string;
  // The receipt's uuid, when the document was issued for one.
  orderUuid?: string;
  // The made-up fiscal number the receipt got.
  fiscalNumber?: string;
  // getDocumentKey of the real versions already queued for the backend.
  delivered?: string[];
  createdAt: string;
}

export interface RealDocumentDelivery {
  id: string;
  xml: string;
  outboxSent?: boolean;
  receipt?: ReceiptFiscalDocumentUpdate;
  attempts: number;
  createdAt: string;
}

const DOCUMENTS_KEY = 'TAX_FAKE_PRRO_DOCUMENTS';
const SESSIONS_KEY = 'TAX_FAKE_PRRO_SESSIONS';
const DELIVERIES_KEY = 'TAX_FAKE_PRRO_DELIVERIES';
// How long fake documents are remembered: by then the outbox has long had their staged copies.
const KEEP_MILLISECONDS = 60 * 24 * 60 * 60 * 1000;
const MAX_SESSIONS = 200;

@Injectable({ providedIn: 'root' })
export class FakePrroStore {
  private _writes: Promise<unknown> = Promise.resolve();

  constructor(private _storage: CompanyStorage) {}

  async getDocuments(): Promise<Record<string, FakePrroDocument>> {
    return (await this._storage.get(DOCUMENTS_KEY)) || {};
  }

  async getDocument(uid: string): Promise<FakePrroDocument | null> {
    return (await this.getDocuments())[this._key(uid)] || null;
  }

  // Adds the document, or merges the given values into the one with its UID.
  saveDocument(
    document: Partial<FakePrroDocument> & { uid: string },
  ): Promise<void> {
    return this._write(async () => {
      const documents = await this.getDocuments();
      const cutoff = Date.now() - KEEP_MILLISECONDS;
      for (const [key, value] of Object.entries(documents)) {
        if (!(Date.parse(value.createdAt) >= cutoff)) {
          delete documents[key];
        }
      }
      const uid = this._key(document.uid);
      const values = Object.fromEntries(
        Object.entries(document).filter(([, value]) => value !== undefined),
      );
      documents[uid] = {
        createdAt: new Date().toISOString(),
        ...documents[uid],
        ...values,
        uid,
      } as FakePrroDocument;
      await this._storage.set(DOCUMENTS_KEY, documents);
    });
  }

  addSession(sessionId: string): Promise<void> {
    return this._write(async () => {
      const sessions: string[] = (await this._storage.get(SESSIONS_KEY)) || [];
      if (!sessions.includes(String(sessionId))) {
        sessions.push(String(sessionId));
        await this._storage.set(SESSIONS_KEY, sessions.slice(-MAX_SESSIONS));
      }
    });
  }

  async isSession(sessionId: string | number | null | undefined): Promise<boolean> {
    if (sessionId === null || sessionId === undefined || sessionId === '') {
      return false;
    }
    const sessions: string[] = (await this._storage.get(SESSIONS_KEY)) || [];
    return sessions.includes(String(sessionId));
  }

  // Queues a fake document's real version for the backend, once per documentKey. Returns false
  // when it was queued before.
  queueRealDocument(
    uid: string,
    documentKey: string,
    xml: string,
    receipt?: ReceiptFiscalDocumentUpdate,
  ): Promise<boolean> {
    return this._write(async () => {
      const documents = await this.getDocuments();
      const document = documents[this._key(uid)];
      if (!document || document.delivered?.includes(documentKey)) {
        return false;
      }
      document.delivered = [...(document.delivered || []), documentKey];
      const deliveries = await this.getDeliveries();
      deliveries.push({
        id: UUID.UUID(),
        xml,
        receipt,
        attempts: 0,
        createdAt: new Date().toISOString(),
      });
      await this._storage.set(DELIVERIES_KEY, deliveries);
      await this._storage.set(DOCUMENTS_KEY, documents);
      return true;
    });
  }

  async getDeliveries(): Promise<RealDocumentDelivery[]> {
    return (await this._storage.get(DELIVERIES_KEY)) || [];
  }

  // Saves the delivery's progress; with requeue, it also moves to the end of the queue, so a
  // delivery that keeps failing does not hold back the others.
  updateDelivery(delivery: RealDocumentDelivery, requeue = false): Promise<void> {
    return this._write(async () => {
      const deliveries = await this.getDeliveries();
      const index = deliveries.findIndex((item) => item.id === delivery.id);
      if (index < 0) {
        return;
      }
      if (requeue) {
        deliveries.splice(index, 1);
        deliveries.push(delivery);
      } else {
        deliveries[index] = delivery;
      }
      await this._storage.set(DELIVERIES_KEY, deliveries);
    });
  }

  // Drops a register's queued deliveries and the given made-up session ids. Returns how many
  // deliveries were dropped. Its fake documents stay: their staged copies must still be kept out
  // of the outbox, and they expire on their own.
  clearRegister(
    taxRegisterFiscalNumber: string,
    sessionIds: (string | number | null | undefined)[] = [],
  ): Promise<number> {
    return this._write(async () => {
      const register = String(taxRegisterFiscalNumber ?? '').trim();
      const deliveries = await this.getDeliveries();
      const remaining = deliveries.filter(
        (item) =>
          String(
            item.receipt?.taxRegisterFiscalNumber ||
              readDocumentIdentity(item.xml).cashRegisterNum,
          ).trim() !== register,
      );
      if (remaining.length !== deliveries.length) {
        await this._storage.set(DELIVERIES_KEY, remaining);
      }

      const drop = sessionIds
        .filter((id) => id !== null && id !== undefined && id !== '')
        .map(String);
      const sessions: string[] = (await this._storage.get(SESSIONS_KEY)) || [];
      if (sessions.some((id) => drop.includes(id))) {
        await this._storage.set(
          SESSIONS_KEY,
          sessions.filter((id) => !drop.includes(id)),
        );
      }
      return deliveries.length - remaining.length;
    });
  }

  removeDelivery(id: string): Promise<void> {
    return this._write(async () => {
      const deliveries = await this.getDeliveries();
      await this._storage.set(
        DELIVERIES_KEY,
        deliveries.filter((item) => item.id !== id),
      );
    });
  }

  // TaxService and the worker change the same keys: one write at a time.
  private _write<T>(task: () => Promise<T>): Promise<T> {
    const run = this._writes.catch(() => undefined).then(task);
    this._writes = run;
    return run;
  }

  private _key(uid: string): string {
    return String(uid ?? '').trim().toLowerCase();
  }
}
